// src/Services/AbpnClient.ts
import { configService } from "./Config/ConfigService";

export interface AuthLike {
  isAuthenticated?: () => boolean;
  getAccessToken?: (scopes?: string[]) => Promise<string | null>;
  getNativeSession?: () => { token: string } | null;
  getOnBehalfOf?: () => string | null;
  getOboToken?: () => string | null;
}

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

class AbpnClient {
  private async getBaseUrl(): Promise<string> {
    const cfg: any = await configService.getConfiguration();

    const base =
      cfg.PreCertApiUrl ||
      cfg.AbpnApiUrl ||
      cfg.ApiBaseUrl ||
      "";

    return (base as string).replace(/\/+$/, "");
  }

  // -------- Basic JSON helpers (back-compat signatures) --------

  public async get<T>(
    path: string,
    auth?: AuthLike | null,
    _unused?: any,
    signal?: AbortSignal
  ): Promise<T> {
    return this.request<T>("GET", path, undefined, auth ?? undefined, signal);
  }

  public async post<T>(
    path: string,
    body?: any,
    auth?: AuthLike | null,
    signal?: AbortSignal
  ): Promise<T> {
    return this.request<T>("POST", path, body, auth ?? undefined, signal);
  }

  public async put<T>(
    path: string,
    body?: any,
    auth?: AuthLike | null,
    signal?: AbortSignal
  ): Promise<T> {
    return this.request<T>("PUT", path, body, auth ?? undefined, signal);
  }

  public async delete<T>(
    path: string,
    auth?: AuthLike | null,
    signal?: AbortSignal
  ): Promise<T> {
    return this.request<T>("DELETE", path, undefined, auth ?? undefined, signal);
  }

  // -------- getResponse for file downloads --------

  public async getResponse(
    path: string,
    auth?: AuthLike | null,
    signal?: AbortSignal
  ): Promise<Response> {
    const baseUrl = await this.getBaseUrl();
    const url =
      path.startsWith("http://") || path.startsWith("https://")
        ? path
        : `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;

    const headers = await this.getDefaultHeaders(auth ?? undefined);

    const init: RequestInit = {
      method: "GET",
      headers,
      credentials: "include",
    };
    if (signal) {
      init.signal = signal;
    }

    console.log("[AbpnClient] getResponse", {
      url,
      oboId: (headers as any)["x-obo-preCertUserId"] ?? null,
      hasOboToken: !!(headers as any)["x-admin-obo-token"],
      hasAuthHeader: !!(headers as any)["Authorization"],
    });

    return fetch(url, init);
  }

  // -------- postForm for file uploads (multipart/form-data) --------

  public async postForm<T>(
    path: string,
    formData: FormData,
    auth?: AuthLike | null,
    signal?: AbortSignal
  ): Promise<T> {
    const baseUrl = await this.getBaseUrl();
    const url =
      path.startsWith("http://") || path.startsWith("https://")
        ? path
        : `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;

    const headers = await this.getDefaultHeaders(auth ?? undefined);
    // Let browser set multipart boundary; keep Accept only
    delete headers["Content-Type"];

    const init: RequestInit = {
      method: "POST",
      headers,
      body: formData,
      credentials: "include",
    };
    if (signal) {
      init.signal = signal;
    }

    console.log("[AbpnClient] postForm", {
      url,
      oboId: (headers as any)["x-obo-preCertUserId"] ?? null,
      hasOboToken: !!(headers as any)["x-admin-obo-token"],
      hasAuthHeader: !!(headers as any)["Authorization"],
    });

    const resp = await fetch(url, init);

    if (!resp.ok) {
      const text = await resp.text();
      console.error("[AbpnClient] postForm response not OK", {
        url,
        status: resp.status,
        text,
      });

      let message = `Request failed (${resp.status})`;
      try {
        if (text) {
          const parsed = JSON.parse(text);
          if (parsed?.title || parsed?.detail) {
            message = parsed.detail || parsed.title || message;
          }
        }
      } catch {
        // ignore parse failure
      }

      throw new Error(message);
    }

    const contentType = resp.headers.get("content-type") || "";
    const raw = await resp.text();
    if (!raw) {
      return undefined as unknown as T;
    }

    if (contentType.includes("application/json")) {
      return JSON.parse(raw) as T;
    }

    return (raw as unknown) as T;
  }

  // -------- Core JSON request helper --------

  private async request<T>(
    method: HttpMethod,
    path: string,
    body?: any,
    auth?: AuthLike,
    signal?: AbortSignal
  ): Promise<T> {
    const baseUrl = await this.getBaseUrl();

    const url =
      path.startsWith("http://") || path.startsWith("https://")
        ? path
        : `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;

    const headers = await this.getDefaultHeaders(auth);

    const init: RequestInit = {
      method,
      headers,
      credentials: "include", // send cookies
    };

    if (signal) {
      init.signal = signal;
    }

    if (body !== undefined && body !== null && method !== "GET") {
      init.body = typeof body === "string" ? body : JSON.stringify(body);
    }

    console.log("[AbpnClient] request", {
      method,
      url,
      hasAuthHeader: !!(headers as any)["Authorization"],
      oboId: (headers as any)["x-obo-preCertUserId"] ?? null,
      hasOboToken: !!(headers as any)["x-admin-obo-token"],
    });

    const resp = await fetch(url, init);

    if (!resp.ok) {
      const text = await resp.text();
      console.error("[AbpnClient] response not OK", {
        url,
        status: resp.status,
        text,
      });

      let message = `Request failed (${resp.status})`;
      try {
        if (text) {
          const parsed = JSON.parse(text);
          if (parsed?.title || parsed?.detail) {
            message = parsed.detail || parsed.title || message;
          }
        }
      } catch {
        // ignore parse failure
      }

      throw new Error(message);
    }

    if (resp.status === 204) {
      return undefined as unknown as T;
    }

    const contentType = resp.headers.get("content-type") || "";
    const raw = await resp.text();
    if (!raw) {
      return undefined as unknown as T;
    }

    if (contentType.includes("application/json")) {
      return JSON.parse(raw) as T;
    }

    return (raw as unknown) as T;
  }

  private async getDefaultHeaders(
    auth?: AuthLike
  ): Promise<Record<string, string>> {
    const headers: Record<string, string> = {
      Accept: "application/json",
      "Content-Type": "application/json",
      Pragma: "no-cache",
    };

    // 1) Bearer (MSAL or native)
    let tokenAttached = false;

    if (auth?.isAuthenticated && auth.getAccessToken) {
      try {
        const token = await auth.getAccessToken();
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
          tokenAttached = true;
        }
      } catch (e) {
        console.warn(
          "[AbpnClient] failed to get MSAL access token",
          e
        );
      }
    }

    if (!tokenAttached && auth?.getNativeSession) {
      const native = auth.getNativeSession();
      if (native?.token) {
        headers["Authorization"] = `Bearer ${native.token}`;
        tokenAttached = true;
      }
    }

    // 2) OBO headers
    const oboId = auth?.getOnBehalfOf?.();
    const oboToken = auth?.getOboToken?.();

    if (oboId) {
      headers["x-obo-preCertUserId"] = oboId;
    }
    if (oboToken) {
      headers["x-admin-obo-token"] = oboToken;
    }

    return headers;
  }
}

const instance = new AbpnClient();
export const abpnClient = instance;
export default instance;
